import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Route,
  Users,
  BarChart2,
  Building2,
  Settings,
  LogOut,
  Plus,
  ChevronDown,
  WalletCards,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import restauragroLogo from '../../assets/restauragro-logo.png';
import toast from 'react-hot-toast';

const navItems = [
  { to: '/projects', icon: FolderKanban, label: 'Projetos' },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Painel' },
  { to: '/finance', icon: WalletCards, label: 'Financeiro' },
  { to: '/my-tasks', icon: CheckSquare, label: 'Minhas Tarefas' },
  { to: '/mileage', icon: Route, label: 'Quilometragem' },
  { to: '/team', icon: Users, label: 'Equipe' },
  { to: '/reports', icon: BarChart2, label: 'Relatórios' },
];

function AppLogo() {
  return (
    <div className="flex items-center">
      <img src={restauragroLogo} alt="RestaurAgro" className="h-12 w-12 object-contain" />
    </div>
  );
}

export function Sidebar() {
  const { user, logout } = useAuthStore();
  const { workspaces, activeWorkspaceId, fetchWorkspaces, createWorkspace, setActiveWorkspace } = useWorkspaceStore();
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const navigate = useNavigate();
  const activeWorkspace = workspaces.find(workspace => workspace.id === activeWorkspaceId);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canAccessFinance = user?.role === 'finance' || user?.role === 'super_admin';
  const baseNavItems = navItems.filter(item => item.to !== '/finance' || canAccessFinance);
  const visibleNavItems = user?.role === 'super_admin'
    ? [...baseNavItems, { to: '/admin/companies', icon: Building2, label: 'Gestão da Plataforma' }]
    : baseNavItems.filter(item => isAdmin || !['/team', '/reports'].includes(item.to));

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces, user?.organization_id]);

  const handleCreateWorkspace = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workspaceName.trim()) return;
    setCreatingWorkspace(true);
    try {
      await createWorkspace({ name: workspaceName.trim() });
      setWorkspaceName('');
      setShowCreateWorkspace(false);
      navigate('/projects');
      toast.success('Área de trabalho criada!');
    } catch {
      toast.error('Falha ao criar área de trabalho');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  return (
    <>
    <aside className="fixed left-0 top-0 z-30 flex h-full w-64 flex-col border-r border-[#D0D4E4] bg-[#F7F9FF] text-[#323338]">
      <div className="px-5 py-3">
        <AppLogo />
      </div>

      <nav className="px-3 pb-4">
        <ul className="space-y-1">
          {visibleNavItems.map(({ to, icon: Icon, label }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#e2e9e4] text-[#3b684a]'
                      : 'text-[#676879] hover:bg-white hover:text-[#323338]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={18} className={isActive ? 'text-[#49835d]' : 'text-[#676879]'} />
                    <span className="truncate">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="px-3 pb-4">
        <div className="mb-2 flex items-center justify-between px-2">
          <p className="text-xs font-bold text-[#323338]">Área de trabalho</p>
          <button
            type="button"
            onClick={() => setShowCreateWorkspace(true)}
            className="rounded p-1 text-[#676879] transition-colors hover:bg-white hover:text-[#C24914]"
            aria-label="Adicionar área de trabalho"
            title="Adicionar área de trabalho"
          >
            <Plus size={15} />
          </button>
        </div>

        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowWorkspaceMenu(open => !open)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded border border-[#D0D4E4] bg-white px-2 py-2 text-left text-sm font-semibold text-[#323338] shadow-sm"
            aria-expanded={showWorkspaceMenu}
            aria-label="Selecionar área de trabalho"
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-xs font-black text-white"
              style={{ backgroundColor: activeWorkspace?.color || '#FF642E' }}
            >
              {activeWorkspace?.name.slice(0, 2).toUpperCase() || 'AT'}
            </span>
            <span className="truncate">{activeWorkspace?.name || 'Área principal'}</span>
            <ChevronDown size={15} className="ml-auto shrink-0 text-[#676879]" />
          </button>
          <button
            type="button"
            onClick={() => navigate('/projects?create=1')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#C24914] text-white transition-colors hover:bg-[#A83D10]"
            aria-label="Criar projeto na área ativa"
            title="Criar projeto na área ativa"
          >
            <Plus size={18} />
          </button>

          {showWorkspaceMenu && (
            <div className="absolute left-0 right-11 top-11 z-40 overflow-hidden rounded-lg border border-[#D0D4E4] bg-white shadow-lg">
              {workspaces.map(workspace => (
                <button
                  key={workspace.id}
                  type="button"
                  onClick={() => {
                    setActiveWorkspace(workspace.id);
                    setShowWorkspaceMenu(false);
                    navigate('/projects');
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#f9f3f0] ${
                    workspace.id === activeWorkspaceId ? 'bg-[#e2e9e4] font-semibold' : ''
                  }`}
                >
                  <span className="h-3 w-3 rounded" style={{ backgroundColor: workspace.color }} />
                  <span className="truncate">{workspace.name}</span>
                  {workspace.is_main && <span className="ml-auto text-[10px] text-[#676879]">Principal</span>}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setShowWorkspaceMenu(false); setShowCreateWorkspace(true); }}
                className="flex w-full items-center gap-2 border-t border-[#D0D4E4] px-3 py-2 text-left text-sm text-[#C24914] hover:bg-[#f9f3f0]"
              >
                <Plus size={14} />
                Nova área de trabalho
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1" />

      <div className="border-t border-[#D0D4E4] p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C24914] text-sm font-bold text-white">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#323338]">{user?.name}</p>
            <p className="truncate text-xs text-[#676879]">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <NavLink to="/settings" className="flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-xs text-[#676879] transition-colors hover:bg-white hover:text-[#C24914]">
            <Settings size={14} />
            Configurações
          </NavLink>
          <button
            onClick={logout}
            className="flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-xs text-[#676879] transition-colors hover:bg-white hover:text-[#C24914]"
          >
            <LogOut size={14} />
            Sair
          </button>
        </div>
      </div>
    </aside>
    <Modal isOpen={showCreateWorkspace} onClose={() => setShowCreateWorkspace(false)} title="Nova área de trabalho" size="sm">
      <form onSubmit={handleCreateWorkspace} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Nome *</label>
          <input
            autoFocus
            required
            value={workspaceName}
            onChange={event => setWorkspaceName(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Ex.: Comercial, Fazenda Norte..."
          />
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={() => setShowCreateWorkspace(false)} className="flex-1 justify-center">
            Cancelar
          </Button>
          <Button type="submit" loading={creatingWorkspace} className="flex-1 justify-center">
            Criar área
          </Button>
        </div>
      </form>
    </Modal>
    </>
  );
}
